import { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import { Company, Dictionary } from '~/types/general';
import { IJCState } from '~/types/models/jcState';
import { IJCStatus } from '~/types/models/jcStatus';
import { IReportType } from '~/types/models/reportType';
import { MainState } from '.';
import {
  fetchBranches,
  fetchJcStates,
  fetchJcStatuses,
  fetchCompanies,
  fetchReportTypes,
  fetchLogo,
  fetchDepartments,
  fetchSubDepartments,
  fetchWorkstations,
} from './actions';
import { JCStatesAdapter, JCStatusesAdapter, ReportTypesAdapter } from './adapters';
import { IDepartmentResponse } from '~/types/response/department';
import { ISubDepartmentResponse } from '~/types/response/subDepartment';
import { IWorkstationResponse } from '~/types/response/workstation';

export const extraReducers = (builder: ActionReducerMapBuilder<MainState>) => {
  /* Statuses */
  builder.addCase(fetchJcStatuses.pending, (state: MainState) => {
    state.loaders.jcStatuses = true;
  });
  builder.addCase(fetchJcStatuses.fulfilled, (state: MainState, { payload }: PayloadAction<IJCStatus[]>) => {
    JCStatusesAdapter.setAll(state.dictionaries.jcStatuses, payload);
    state.loaders.jcStatuses = false;
  });
  builder.addCase(fetchJcStatuses.rejected, (state: MainState) => {
    state.loaders.jcStatuses = false;
  });

  /* States */
  builder.addCase(fetchJcStates.pending, (state: MainState) => {
    state.loaders.jcStates = true;
  });
  builder.addCase(fetchJcStates.fulfilled, (state: MainState, { payload }: PayloadAction<IJCState[]>) => {
    JCStatesAdapter.setAll(state.dictionaries.jcStates, payload);
    state.loaders.jcStates = false;
  });
  builder.addCase(fetchJcStates.rejected, (state: MainState) => {
    state.loaders.jcStates = false;
  });

  /* Branches */
  builder.addCase(fetchBranches.pending, (state: MainState) => {
    state.loaders.branches = true;
  });
  builder.addCase(fetchBranches.fulfilled, (state: MainState, { payload }: PayloadAction<Dictionary[]>) => {
    state.dictionaries.branches = payload;
    state.loaders.branches = false;
  });
  builder.addCase(fetchBranches.rejected, (state: MainState) => {
    state.loaders.branches = false;
  });

  /* Companies */
  builder.addCase(fetchCompanies.pending, (state: MainState) => {
    state.loaders.companies = true;
  });
  builder.addCase(fetchCompanies.fulfilled, (state: MainState, { payload }: PayloadAction<Company[]>) => {
    state.dictionaries.companies = payload;
    state.loaders.companies = false;
  });
  builder.addCase(fetchCompanies.rejected, (state: MainState) => {
    state.loaders.companies = false;
  });

  /* Report Types */
  builder.addCase(fetchReportTypes.pending, (state: MainState) => {
    state.dictionaries.reportTypes.loading = true;
  });
  builder.addCase(fetchReportTypes.fulfilled, (state: MainState, { payload }: PayloadAction<IReportType[]>) => {
    ReportTypesAdapter.setAll(state.dictionaries.reportTypes, payload);
    state.dictionaries.reportTypes.loading = false;
  });
  builder.addCase(fetchReportTypes.rejected, (state: MainState) => {
    state.dictionaries.reportTypes.loading = false;
  });
  builder.addCase(fetchLogo.fulfilled, (state: MainState, { payload }: PayloadAction<string>) => {
    state.logo = payload;
  });

  /* Departments */
  builder.addCase(fetchDepartments.pending, (state: MainState) => {
    state.loaders.departments = true;
  });
  builder.addCase(fetchDepartments.fulfilled, (state: MainState, { payload }: PayloadAction<IDepartmentResponse[]>) => {
    state.dictionaries.departments = payload;
    state.loaders.departments = false;
  });
  builder.addCase(fetchDepartments.rejected, (state: MainState) => {
    state.loaders.departments = false;
  });

  /* SubDepartments */
  builder.addCase(fetchSubDepartments.pending, (state: MainState) => {
    state.loaders.subDepartments = true;
  });
  builder.addCase(
    fetchSubDepartments.fulfilled,
    (state: MainState, { payload }: PayloadAction<ISubDepartmentResponse[]>) => {
      state.dictionaries.subDepartments = payload;
      state.loaders.subDepartments = false;
    },
  );
  builder.addCase(fetchSubDepartments.rejected, (state: MainState) => {
    state.loaders.subDepartments = false;
  });

  /* Workstations */
  builder.addCase(fetchWorkstations.pending, (state: MainState) => {
    state.loaders.workstations = true;
  });
  builder.addCase(
    fetchWorkstations.fulfilled,
    (state: MainState, { payload }: PayloadAction<IWorkstationResponse[]>) => {
      state.dictionaries.workstations = payload;
      state.loaders.workstations = false;
    },
  );
  builder.addCase(fetchWorkstations.rejected, (state: MainState) => {
    state.loaders.workstations = false;
  });
};
